import { Form, Input, Radio, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { requestCredential, updateCredentialBindings } from '@/api/credential'
import type { ServerCredentialInfo, ServerDeploymentInfo } from '@/api/type-server'
import { CredentialResourceType } from '@/api/type-server'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Checkbox } from '@/components/base/Checkbox'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { EmptyView } from '@/components/EmptyView'
import { canBindCredential } from '@/utils/credential'
import { cn } from '@/utils/style'

interface CredentialModalProps {
  credential?: ServerCredentialInfo
  onClose: () => void
  onSaved: () => void
}

function resolveInitialExpiration(validDurationSec?: number) {
  if (validDurationSec == null) return { selected: 30 * 24 * 3600, customDays: '' }
  const presets = [30 * 24 * 3600, 90 * 24 * 3600, 180 * 24 * 3600, 0]
  if (presets.includes(validDurationSec)) return { selected: validDurationSec, customDays: '' }
  return { selected: -1, customDays: String(Math.round(validDurationSec / (24 * 3600))) }
}

function resolveInitialBindingStrategy(credential?: ServerCredentialInfo) {
  if (!credential) return 'auto'
  const hasAutoBinding = credential.resources?.some(
    (r) => r.resourceType === CredentialResourceType.DeploymentClass,
  )
  return hasAutoBinding ? 'auto' : 'manual'
}

function resolveInitialBoundIds(credential?: ServerCredentialInfo) {
  const ids = new Set<string>()
  credential?.resources?.forEach((r) => {
    if (r.resourceType === CredentialResourceType.Deployment) {
      r.resIDs?.forEach((id) => ids.add(id))
    }
  })
  return ids
}

export function CredentialModal({ credential, onClose, onSaved }: CredentialModalProps) {
  const isEdit = !!credential
  const { t } = useTranslation('accessKeys')
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ ak: string; sk: string } | null>(null)

  const [selectedExpiration, setSelectedExpiration] = useState(
    () => resolveInitialExpiration(credential?.validDurationSec).selected,
  )
  const [customDays, setCustomDays] = useState(
    () => resolveInitialExpiration(credential?.validDurationSec).customDays,
  )

  // Binding strategy
  const [bindingStrategy, setBindingStrategy] = useState<'auto' | 'manual'>(() =>
    resolveInitialBindingStrategy(credential),
  )
  const [deployments, setDeployments] = useState<ServerDeploymentInfo[]>([])
  const [deploymentsLoading, setDeploymentsLoading] = useState(false)
  const [selectedDeployIds, setSelectedDeployIds] = useState<Set<string>>(() =>
    resolveInitialBoundIds(credential),
  )
  const bindingAvailable = !credential || canBindCredential(credential)
  const [customDaysError, setCustomDaysError] = useState('')

  const fetchDeployments = useCallback(async () => {
    setDeploymentsLoading(true)
    try {
      const res = await API.getDeploymentList({ enabled: true, page: 1, per_page: 100 })
      if (res.code === 0) {
        setDeployments(res.deployments || [])
      }
    } finally {
      setDeploymentsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (bindingStrategy === 'manual') {
      fetchDeployments()
    }
  }, [bindingStrategy, fetchDeployments])

  const SCOPE_OPTIONS = [
    { label: t('createModal.scopeAutoDeploy'), value: 'auto_deploy', disabled: false },
    { label: t('createModal.scopeAutoScaling'), value: 'auto_scaling', disabled: true },
    { label: t('createModal.scopeInstance'), value: 'instance', disabled: true },
  ]

  const MAX_CUSTOM_DAYS = 3650

  const EXPIRATION_OPTIONS = [
    { value: 30 * 24 * 3600, display: '30', unit: t('createModal.days') },
    { value: 90 * 24 * 3600, display: '90', unit: t('createModal.days') },
    { value: 180 * 24 * 3600, display: '180', unit: t('createModal.days') },
    { value: 0, display: t('createModal.never'), unit: '' },
    { value: -1, display: t('createModal.custom'), unit: '' },
  ]

  const BINDING_OPTIONS = [
    { label: t('createModal.bindingAuto'), value: 'auto' },
    { label: t('createModal.bindingManual'), value: 'manual' },
  ]

  // Deployment table helpers
  const allSelected = deployments.length > 0 && selectedDeployIds.size === deployments.length
  const someSelected = selectedDeployIds.size > 0 && selectedDeployIds.size < deployments.length

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedDeployIds(new Set())
    } else {
      setSelectedDeployIds(new Set(deployments.map((d) => d.resID)))
    }
  }

  const handleToggle = (resID: string) => {
    setSelectedDeployIds((prev) => {
      const next = new Set(prev)
      if (next.has(resID)) {
        next.delete(resID)
      } else {
        next.add(resID)
      }
      return next
    })
  }

  const initialStrategy = resolveInitialBindingStrategy(credential)
  const confirmAutoBinding = () =>
    Modal.confirm({
      title: t('createModal.autoConfirm.title'),
      content: <p>{t('createModal.autoConfirm.content')}</p>,
      confirmBtnCfg: { type: 'secondary', text: t('createModal.autoConfirm.confirmBtn') },
    }).then(
      () => true,
      () => false,
    )

  const handleSubmit = async () => {
    const values = await form.validateFields().catch(() => null)
    if (!values) return
    let updated = false
    try {
      setLoading(true)
      if (selectedExpiration === -1) {
        const n = Number(customDays)
        if (!customDays || !Number.isInteger(n) || n < 1) {
          setCustomDaysError(t('createModal.customMinError'))
          return
        }
        if (n > MAX_CUSTOM_DAYS) {
          setCustomDaysError(t('createModal.customMaxError', { max: MAX_CUSTOM_DAYS }))
          return
        }
      }
      const TEN_YEARS_SEC = 10 * 365 * 24 * 3600
      const durationSec =
        selectedExpiration === -1
          ? Number(customDays) * 24 * 3600
          : selectedExpiration === 0
            ? TEN_YEARS_SEC
            : selectedExpiration

      if (isEdit) {
        if (initialStrategy === 'manual' && bindingStrategy === 'auto') {
          if (!(await confirmAutoBinding())) return
        }

        const credentialChanged =
          values.title !== credential.title ||
          (values.description || '') !== (credential.description || '') ||
          durationSec !== credential.validDurationSec

        if (credentialChanged) {
          const { credential: latest } = await requestCredential((config) =>
            API.getCredential({ res_id: credential.resID }, config),
          )
          await requestCredential((config) =>
            API.updateCredential(
              {
                res_id: credential.resID,
                title: values.title,
                description: values.description || '',
                valid_duration_sec: durationSec,
                enabled: latest.enabled,
              },
              config,
            ),
          )
          updated = true
        }

        await updateCredentialBindings(credential.resID, bindingStrategy, selectedDeployIds)

        message.success(t('editModal.updated'))
        onSaved()
        onClose()
      } else {
        if (bindingStrategy === 'auto') {
          if (!(await confirmAutoBinding())) return
        }

        const resources =
          bindingStrategy === 'auto'
            ? [{ res_type: CredentialResourceType.DeploymentClass }]
            : [{ res_ids: [...selectedDeployIds], res_type: CredentialResourceType.Deployment }]

        const res = await requestCredential((config) =>
          API.createCredential(
            {
              title: values.title,
              description: values.description || '',
              valid_duration_sec: durationSec,
              resources,
            },
            config,
          ),
        )

        setResult({ ak: res.accessKeyEncoded, sk: res.secretKeyEncoded })
        onSaved()
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : t('errors.requestFailed')
      message.error(updated ? `${t('errors.detailsSaved')} ${reason}` : reason)
      if (isEdit) onSaved()
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(t('copied'))
  }

  const handleClose = () => {
    onClose()
  }

  if (result) {
    return (
      <Modal className="w-[640px]" open={true} onClose={handleClose}>
        <Modal.Header>{t('createdModal.title')}</Modal.Header>
        <Modal.Body>
          <p className="t-sm text-secondary mb-4">
            {t('createdModal.warning')}
            <span className="text-highlight">{t('createdModal.warningHighlight')}</span>
          </p>
          <div className="flex flex-col gap-2">
            <div>
              <div className="t-sb text-primary mb-2">{t('createdModal.ak')}</div>
              <div className="flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3">
                <span className="flex-1 break-all t-sm text-primary">{result.ak}</span>
                <CopyIcon
                  className="w-5 h-5 text-tertiary cursor-pointer shrink-0 hover:text-secondary"
                  onClick={() => handleCopy(result.ak)}
                />
              </div>
            </div>
            <div>
              <div className="t-sb text-primary mb-2">{t('createdModal.sk')}</div>
              <div className="flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3">
                <span className="flex-1 break-all t-sm text-primary">{result.sk}</span>
                <CopyIcon
                  className="w-5 h-5 text-tertiary cursor-pointer shrink-0 hover:text-secondary"
                  onClick={() => handleCopy(result.sk)}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="secondary" onClick={handleClose} className="min-w-[88px]">
            {t('createdModal.okBtn')}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal
      className="w-[640px] max-h-[calc(100dvh-32px)]"
      open={true}
      onClose={loading ? undefined : onClose}
    >
      <Modal.Header className="shrink-0">
        {isEdit ? t('editModal.title') : t('createModal.title')}
      </Modal.Header>
      <Modal.Body scrollable showScrollbar className="min-h-0">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="flex flex-col gap-4"
          initialValues={
            isEdit ? { title: credential.title, description: credential.description } : undefined
          }
        >
          {/* Scope */}
          <div>
            <div className="t-sb text-primary mb-2">
              {t('createModal.scope')} <span className="text-error">*</span>
            </div>
            <Radio.Group defaultValue="auto_deploy" className="flex gap-6">
              {SCOPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={cn(
                    'flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3 flex-1',
                    opt.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:border-gray300',
                  )}
                >
                  <Radio value={opt.value} disabled={opt.disabled} />
                  <span className="t-sb text-primary">{opt.label}</span>
                </label>
              ))}
            </Radio.Group>
          </div>

          {/* Name */}
          <Form.Item
            name="title"
            label={
              <span className="t-sb text-primary">
                {t('createModal.name')} <span className="text-error">*</span>
              </span>
            }
            rules={[{ required: true, message: t('createModal.nameRequired') }]}
            className="!mb-0"
          >
            <Input
              placeholder={t('createModal.namePlaceholder')}
              className="h-[44px] t-sb placeholder:text-tertiary"
              maxLength={32}
            />
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label={
              <span className="t-sb text-primary">
                {t('createModal.description')} <span className="text-error">*</span>
              </span>
            }
            rules={[{ required: true, message: t('createModal.descriptionRequired') }]}
            className="!mb-0"
          >
            <Input.TextArea
              placeholder={t('createModal.descriptionPlaceholder')}
              rows={3}
              className="t-sb placeholder:text-tertiary"
              maxLength={384}
              showCount
            />
          </Form.Item>

          {/* Expiration */}
          <div>
            <div className="t-sb text-primary mb-2">
              {t('createModal.expiration')} <span className="text-error">*</span>
            </div>
            <div className="flex gap-2">
              {EXPIRATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    'flex-1 h-[44px] border border-gray100 rounded-lg t-sm flex items-center justify-center gap-1 cursor-pointer transition-colors',
                    selectedExpiration === opt.value
                      ? 'border-secondary bg-highlight/10 text-highlight'
                      : 'bg-panel-white text-primary hover:border-gray300',
                  )}
                  onClick={() => setSelectedExpiration(opt.value)}
                >
                  {opt.unit ? (
                    <>
                      <span className="t-bold">{opt.display}</span>
                      <span
                        className={cn(
                          't-sm',
                          selectedExpiration === opt.value ? 'text-highlight' : 'text-secondary',
                        )}
                      >
                        {opt.unit}
                      </span>
                    </>
                  ) : (
                    <span className="t-bold">{opt.display}</span>
                  )}
                </button>
              ))}
            </div>
            {selectedExpiration === -1 && (
              <>
                <Input
                  placeholder={t('createModal.customPlaceholder')}
                  className="h-[44px] mt-2 t-sb placeholder:text-tertiary"
                  type="number"
                  value={customDays}
                  min={1}
                  max={MAX_CUSTOM_DAYS}
                  onKeyDown={(e) => {
                    if (['.', 'e', 'E', '-', '+'].includes(e.key)) e.preventDefault()
                  }}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '')
                    if (val !== '' && Number(val) < 1) return
                    setCustomDays(val)
                    if (val !== '' && Number(val) > MAX_CUSTOM_DAYS) {
                      setCustomDaysError(t('createModal.customMaxError', { max: MAX_CUSTOM_DAYS }))
                    } else {
                      setCustomDaysError('')
                    }
                  }}
                  status={customDaysError ? 'error' : undefined}
                  suffix={<span className="t-sm text-secondary">{t('createModal.days')}</span>}
                />
                {customDaysError && <div className="text-error t-sm mt-1">{customDaysError}</div>}
              </>
            )}
          </div>

          {/* Binding Strategy */}
          <div>
            <div className="t-sb text-primary mb-2">
              {t('createModal.bindingStrategy')} <span className="text-error">*</span>
            </div>
            {!bindingAvailable && (
              <p className="text-error t-sm mb-2" role="status">
                {t('errors.bindingUnavailable')}
              </p>
            )}
            <Radio.Group
              value={bindingStrategy}
              onChange={(e) => setBindingStrategy(e.target.value)}
              className="flex gap-6"
            >
              {BINDING_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 border border-gray100 rounded-lg px-4 py-3 flex-1 cursor-pointer hover:border-gray300"
                >
                  <Radio
                    value={opt.value}
                    disabled={
                      !bindingAvailable && opt.value === 'auto' && initialStrategy !== 'auto'
                    }
                  />
                  <span className="t-sb text-primary">{opt.label}</span>
                </label>
              ))}
            </Radio.Group>

            {/* Manual binding: deployment table */}
            {bindingStrategy === 'manual' && (
              <div className="mt-4">
                {deploymentsLoading ? (
                  <div className="flex justify-center items-center h-40">
                    <Loading />
                  </div>
                ) : deployments.length === 0 ? (
                  <EmptyView content={t('bindModal.noDeployments')} />
                ) : (
                  <div className="border border-gray100 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-panel-gray-100">
                          <th className="px-4 py-3 w-[52px]">
                            <Checkbox
                              checked={allSelected}
                              disabled={!bindingAvailable && !allSelected}
                              onChange={handleToggleAll}
                              inputClassName={someSelected ? 'indeterminate' : ''}
                              checkedBgColor="bg-btn-secondary"
                              checkedBorderColor="border-secondary"
                              checkIconColor="after:border-white"
                            />
                          </th>
                          <th className="px-4 py-3 text-left t-sb text-secondary">
                            {t('bindModal.id')}
                          </th>
                          <th className="px-4 py-3 text-left t-sb text-secondary">
                            {t('bindModal.name')}
                          </th>
                          <th className="px-4 py-3 text-left t-sb text-secondary">
                            {t('bindModal.currentAccessKey')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {deployments.map((item) => {
                          const boundCredentials = item.credentials || []
                          const boundNames = boundCredentials.map((c) => c.title).filter(Boolean)
                          const hasBound = boundNames.length > 0
                          return (
                            <tr key={item.resID} className="border-b border-gray100">
                              <td className="px-4 py-4 w-[52px]">
                                <Checkbox
                                  checked={selectedDeployIds.has(item.resID)}
                                  disabled={!bindingAvailable && !selectedDeployIds.has(item.resID)}
                                  onChange={() => handleToggle(item.resID)}
                                  checkedBgColor="bg-btn-secondary"
                                  checkedBorderColor="border-secondary"
                                  checkIconColor="after:border-white"
                                />
                              </td>
                              <td className="px-4 py-4 t-sb text-primary">{item.resID}</td>
                              <td className="px-4 py-4 t-sb text-primary truncate max-w-[200px]">
                                {item.name}
                              </td>
                              <td className="px-4 py-4 t-sb">
                                {boundNames.length > 1 ? (
                                  <Tooltip
                                    title={boundNames.join(', ')}
                                    overlayStyle={{ maxWidth: 240 }}
                                  >
                                    <span className="text-highlight underline decoration-dotted cursor-default">
                                      {t('bindModal.nAccessKeys', { count: boundNames.length })}
                                    </span>
                                  </Tooltip>
                                ) : hasBound ? (
                                  <span className="text-highlight">{boundNames[0]}</span>
                                ) : (
                                  <span className="text-tertiary">{t('bindModal.unbound')}</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="shrink-0">
        <Button type="outline" disabled={loading} onClick={onClose} className="min-w-[88px]">
          {t('createModal.cancelBtn')}
        </Button>
        <Button type="secondary" onClick={handleSubmit} disabled={loading} className="min-w-[88px]">
          {isEdit ? t('editModal.saveBtn') : t('createModal.createBtn')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
